"use client"

import logger from "@/lib/logger/logger-init"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import {
    FOCUS_VISIBLE_CLASS,
    FOCUS_VISIBLE_INSET_CLASS
} from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"
import React, { createContext, useContext, useEffect, useId, useState } from "react"

export const AccordionContext = createContext<{
    isOpen: boolean
    toggle: () => void
    accordionId?: string
    accordionName?: string
    triggerId: string
    contentId: string
} | null>(null)

const AccordionGroupContext = createContext<{
    openAccordionId: string | null
    setOpenAccordionId: (id: string | null) => void
    defaultOpenId?: string
} | null>(null)

type AccordionRootProps = {
    children: React.ReactNode
    initalState?: boolean
    hydratedState?: boolean
    className?: string
    accordionId?: string
    accordionName?: string
    type?: string
    productSku?: string
    productName?: string
    englishProductName?: string
    isGrouped?: boolean
}

export const AccordionRoot = ({
    initalState = false,
    hydratedState = false,
    children,
    className,
    accordionId,
    accordionName,
    type,
    productSku,
    productName,
    englishProductName,
    isGrouped = false
}: AccordionRootProps) => {
    const [isOpen, setIsOpen] = useState(initalState)
    const reactId = useId()
    const triggerId = `${reactId}-trigger`
    const contentId = `${reactId}-content`
    const siteContext = useSiteContext()
    const groupContext = useContext(AccordionGroupContext)

    useEffect(() => {
        setIsOpen(hydratedState)
    }, [hydratedState])

    // If this accordion is part of a group, sync with group state
    useEffect(() => {
        if (isGrouped && groupContext && accordionId) {
            const shouldBeOpen = groupContext.openAccordionId === accordionId
            setIsOpen(shouldBeOpen)
        }
    }, [isGrouped, groupContext, accordionId])

    const toggle = () => {
        const newState = !isOpen

        if (isGrouped && groupContext && accordionId) {
            // In grouped mode, only one accordion can be open at a time
            if (newState) {
                groupContext.setOpenAccordionId(accordionId)
            } else {
                groupContext.setOpenAccordionId(null)
            }
        } else {
            // Individual accordion mode
            setIsOpen(newState)
        }

        // Track accordion event
        if (accordionId && accordionName) {
            import("@/lib/utils/tracking/gtmEvents").then(({ accordionEvent }) => {
                accordionEvent(
                    accordionName,
                    siteContext,
                    type,
                    productSku,
                    englishProductName || productName
                )
            })
        } else {
            logger.warn("Accordion tracking missing required parameters", {
                accordionId,
                accordionName,
                englishProductName
            })
        }
    }

    return (
        <AccordionContext.Provider
            value={{ isOpen, toggle, accordionId, accordionName, triggerId, contentId }}
        >
            <div className={className} data-open={isOpen}>
                {children}
            </div>
        </AccordionContext.Provider>
    )
}

type AccordionGroupWrapperProps = {
    children: React.ReactNode
    className?: string
    defaultOpenId?: string
    firstAccordionOpenByDefault?: boolean
}

export const AccordionGroupWrapper = ({
    children,
    className,
    defaultOpenId,
    firstAccordionOpenByDefault = false
}: AccordionGroupWrapperProps) => {
    const [openAccordionId, setOpenAccordionId] = useState<string | null>(
        firstAccordionOpenByDefault ? "accordion-0" : defaultOpenId || null
    )

    return (
        <AccordionGroupContext.Provider
            value={{ openAccordionId, setOpenAccordionId, defaultOpenId }}
        >
            <div className={className}>{children}</div>
        </AccordionGroupContext.Provider>
    )
}

// AccordionTrigger
type AccordionTriggerProps = {
    children: React.ReactNode
    className?: string
}

export const AccordionTrigger = ({ children, className }: AccordionTriggerProps) => {
    const context = useContext(AccordionContext)
    if (!context) {
        logger.error("AccordionTrigger must be used within an AccordionRoot")
        return null
    }

    const { isOpen, toggle, triggerId, contentId } = context

    return (
        <button
            type="button"
            className={clsx(
                className,
                FOCUS_VISIBLE_CLASS,
                FOCUS_VISIBLE_INSET_CLASS,
                "webkit-tap-highlight-color-transparent"
            )}
            onClick={toggle}
            aria-expanded={isOpen}
            aria-controls={contentId}
            id={triggerId}
        >
            {children}
        </button>
    )
}

//AccordionIcon
type AccordionIconProps = {
    openIcon?: React.ReactNode
    closeIcon?: React.ReactNode
    className?: string
}

export const AccordionIcon = ({ openIcon, closeIcon, className }: AccordionIconProps) => {
    const context = useContext(AccordionContext)
    if (!context) {
        logger.error("AccordionIcon must be used within an AccordionRoot")
        return null
    }

    const { isOpen } = context

    return <div className={className}>{isOpen ? openIcon : closeIcon}</div>
}

// AccordionContent
type AccordionContentProps = {
    children: React.ReactNode
    className?: string
}

export const AccordionContent = ({ children, className }: AccordionContentProps) => {
    const context = useContext(AccordionContext)

    if (!context) {
        logger.error("AccordionContent must be used within an AccordionRoot")
        return null
    }

    const { isOpen, triggerId, contentId } = context

    return (
        <div
            className={clsx(
                "grid transition-all duration-300 ease-in-out",
                isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                className
            )}
            id={contentId}
            role="region"
            aria-labelledby={triggerId}
            inert={!isOpen || undefined}
        >
            <div className="a11y-overflow-clip min-h-0">{children}</div>
        </div>
    )
}
